import { useState } from 'react'
import styled from '@emotion/styled'
import AddIcon from '@mui/icons-material/Add'
import InfoIcon from '@mui/icons-material/Info'
import Collapse from '@mui/material/Collapse'
import Fab from '@mui/material/Fab'
import SnackbarContent from '@mui/material/SnackbarContent'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { bool, func, array } from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { colors } from '@global/constants'
import * as TemplateActions from '../template.actions'
import { NewValidationPicker } from './new-validation-picker'
import { Validation } from './validation'
import { validateLink } from '../../../../utils/link-validator'
import { t } from '@lingui/core/macro'

const AddValidation = styled(Fab)`
  margin-left: 24px;
  height: 65px;
  width: 65px;
`

const AddLinkTypography = styled(Typography)`
  margin-left: 10px;
`

const SnackbarWrapper = styled.div`
  display: flex;
  align-items: center;
`

const Link = styled.a`
  text-decoration: none;
  font-weight: bold;
  margin-left: auto;
`

const useStyles = makeStyles(() => ({
  infoSnackbar: {
    backgroundColor: 'rgb(229, 244, 255)',
    color: colors.blue.main,
    marginBottom: '10px',
    marginTop: '3px',
    maxWidth: '720px',
    boxShadow: 'none',
  },
}))

export const ValidationConfigurator = ({
  updateErrorStatus = () => false,
  saveEnabled = false,
  accounts = [],
  types = [],
  showErrors = false,
  resetShowErrors = () => false,
}) => {
  const configuredValidations = useSelector(state => state.Template.validations)
  const availableValidations = types.filter(type => type.options.length)
  const dispatch = useDispatch()
  const [addValidations, setAddValidations] = useState(false)
  const [newValidation, setNewValidation] = useState(false)
  const classes = useStyles()

  const addValidation = v => {
    dispatch(TemplateActions.addValidation(getDefaultValidation(v)))
    setNewValidation(true)
    setAddValidations(false)
  }

  const resetIsOpen = () => {
    resetShowErrors()
    setNewValidation(false)
  }

  const getDefaultValidation = vId => {
    const defaultValidation = availableValidations.filter(v => v.id == vId)[0] || vId
    return typeof defaultValidation === 'object'
      ? { ...defaultValidation, options: Object.values(defaultValidation.options) }
      : vId
  }

  const configurationErrors = configuredValidations.filter(v => !validateLink(v))

  if (configurationErrors.length && saveEnabled) {
    updateErrorStatus(true)
  } else if (!configurationErrors.length && !saveEnabled) {
    updateErrorStatus(false)
  }

  return (
    <>
      <Typography variant="h6" style={{ marginTop: 24 }}>
        {t`Link validations`}
      </Typography>
      <SnackbarContent
        className={classes.infoSnackbar}
        message={
          <SnackbarWrapper>
            <InfoIcon style={{ marginRight: 10 }} />
            <span>
              {t`Landing page overlays such as cookie acceptance can prevent tag validation from working properly.`}
            </span>
            <Link
              href="https://support.claravine.com/hc/en-us/articles/360058564451-Why-Are-Link-Validations-Failing-Due-to-Cookies"
              target="_blank"
            >
              {t`Learn more`}
            </Link>
          </SnackbarWrapper>
        }
      />
      {configuredValidations.map((validation, index) => {
        const configured = validateLink(validation)
        return (
          <Validation
            accounts={accounts.filter(acct => acct.slug === validation.slug)}
            type={validation}
            key={validation.uuid}
            validations={availableValidations}
            isOpen={
              (newValidation && index == configuredValidations.length - 1) ||
              (showErrors && !configured)
            }
            isConfigured={configured}
            resetIsOpen={resetIsOpen}
          />
        )
      })}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          height: '200px',
          marginTop: 25,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            transition: 'all 150ms ease-out',
          }}
        >
          <AddValidation
            data-test-page-id="add-link-validation"
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => setAddValidations(!addValidations)}
          >
            <AddIcon
              style={{
                transform: addValidations ? 'rotate(45deg' : '',
                transition: 'all 150ms ease-out',
              }}
            />
          </AddValidation>
          {!addValidations && <AddLinkTypography>{t`New link validation`}</AddLinkTypography>}
        </div>
        {addValidations && (
          <Collapse
            in={addValidations}
            style={{ display: 'block', width: '95%', paddingLeft: '20px' }}
          >
            <NewValidationPicker
              selected={-1}
              onSelect={v => addValidation(availableValidations.find(val => val.id == v()))}
              validations={
                configuredValidations.filter(v => v.slug == 'website').length
                  ? availableValidations.filter(v => v.slug !== 'website')
                  : availableValidations
              }
            />
          </Collapse>
        )}
      </div>
    </>
  )
}

ValidationConfigurator.propTypes = {
  updateErrorStatus: func,
  saveEnabled: bool,
  accounts: array,
  types: array,
  showErrors: bool,
  resetShowErrors: func,
}
